import { Tooltip } from 'antd';
import { uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { deleteSupportRole } from '../../../../api/admin/rbac';
import { AutoTable } from '../../../../components/auto-table';
import {
  CommonButton,
  CommonButtonGroup,
  MainButton,
} from '../../../../components/button/main';
import { messageError, messageSuccess } from '../../../../components/message';
import { ModalWrap } from '../../../../components/modal-wrap';
import { CommonTag } from '../../../../components/tag';
import { getLocalTime } from '../../../../lib';
import getFormatMessage from '../../../../locales/get-format-message';
import { RbacRoleType } from '../../../../model';
import {
  IAdminRbacRole,
  IApiAdminRbacRole,
  ISupportUserModel,
  IUserListItem,
} from '../../../../model/api-modules';
import { IRootState } from '../../../../store/reducers';
import { SvgIcon } from '../../../component/svg-icon';
import { SvgIconName } from '../../../component/svg-icon/model';
import { AdminPermCheck } from '../../components/admin-permission-check';
import { EditRoleDrawer } from './drawer';
import styles from './index.module.scss';

interface IProps {
  readonly loading: boolean;
  readonly roles: readonly IApiAdminRbacRole[];
  readonly allUser: readonly IUserListItem[];
  readonly members: readonly ISupportUserModel[];
  readonly onChange: () => void;
}

export const NOTICE_ADMIN_ROLE = 'Notice_Admin';

export const AdminRole = (props: IProps) => {
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const { members, loading, allUser, roles, onChange } = props;

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<IApiAdminRbacRole>();
  const [drawerReadOnly, setDrawerReadOnly] = useState<boolean>(false);

  useEffect(() => {
    if (roles.length <= pageSize * (page - 1)) {
      setPage(1);
    }
  }, [roles.length]);

  const editRole = async (role: IApiAdminRbacRole) => {
    setDrawerReadOnly(false);
    setSelectedRow(role);
    setShowDrawer(true);
  };

  const viewRole = async (role: IApiAdminRbacRole) => {
    setDrawerReadOnly(true);
    setSelectedRow(role);
    setShowDrawer(true);
  };

  const deleteRole = async (role: IApiAdminRbacRole) => {
    if (!userInfo) {
      return;
    }

    ModalWrap.warn({
      title: getFormatMessage('delete_role'),
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
      children: (
        <div style={{ whiteSpace: 'pre-line' }}>
          {getFormatMessage('delete_role_Name_desc', {
            params: { roleName: role.roleName },
          })}
        </div>
      ),
      onOk: async () => {
        const res = await deleteSupportRole({
          projectId: role.projectId,
          roleId: role.roleId,
          operator: userInfo.UserID,
        });
        if (!res) {
          messageError(getFormatMessage('operate_fail'));
          return false;
        }
        onChange();
        return true;
      },
    });
  };

  const add = () => {
    setShowDrawer(true);
    setSelectedRow(undefined);
    setDrawerReadOnly(false);
  };

  const onSave = () => {
    closeDrawer();
    onChange();
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setSelectedRow(undefined);
  };

  const owner = roles.find((role) => role.type === RbacRoleType.Owner);
  const adminRole = roles.find((role) => role.type === RbacRoleType.Admin);
  const commonRoles = roles.filter(
    (role) => ![RbacRoleType.Admin, RbacRoleType.Owner].includes(role.type)
  );
  const isUserSuper = !!userInfo && !!owner?.userList.includes(userInfo.UserID);
  const isUserAdmin =
    !!userInfo && !!adminRole?.userList.includes(userInfo.UserID);

  const commonUsers = uniq(
    roles
      .filter(
        (role) => ![RbacRoleType.Owner, RbacRoleType.Admin].includes(role.type)
      )
      .map((role) => role.userList)
      .flat()
  );

  return (
    <div className={styles.rolePage}>
      <div>
        <AdminPermCheck
          hasPerm={isUserSuper || isUserAdmin}
          contentType="action"
        >
          <MainButton
            icon={<SvgIcon icon={SvgIconName.InviteAddIcon} />}
            onClick={add}
          >
            {getFormatMessage('add_new_role')}
          </MainButton>
        </AdminPermCheck>
      </div>
      <AutoTable
        loading={loading}
        list={roles}
        tableExtension={{
          onChange: (changeInfo) => {
            if (!changeInfo) {
              return;
            }
            setPage(changeInfo.current);
            setPageSize(changeInfo.pageSize || 10);
          },
          pagination: {
            pageSize,
            current: page,
            total: roles.length,
            showTotal: true,
          },
        }}
        configFields={[
          {
            key: 'roleName',
            name: getFormatMessage('role_name'),
            tableExtension: {
              width: '150px',
              renderRow(_key: any, _value: string, row) {
                const roleItem = row as IApiAdminRbacRole;
                const isSysRow = [
                  RbacRoleType.Admin,
                  RbacRoleType.Owner,
                ].includes(roleItem.type);

                if (isSysRow) {
                  return (
                    <span>
                      <CommonButton
                        type="link"
                        onClick={() => viewRole(roleItem)}
                      >
                        {roleItem.roleName}
                      </CommonButton>
                      <CommonTag
                        text={getFormatMessage('access_right_system')}
                        type="gray"
                      ></CommonTag>
                    </span>
                  );
                }
                return (
                  <CommonButton onClick={() => viewRole(roleItem)} type="link">
                    {roleItem.roleName}
                  </CommonButton>
                );
              },
            },
          },
          {
            key: 'desc',
            name: getFormatMessage('description'),
            tableExtension: {
              width: '30%',
              renderRow(_key: any, value: string) {
                return value || '--';
              },
            },
          },
          {
            key: 'member',
            name: getFormatMessage('member'),
            tableExtension: {
              width: '100px',
              renderRow(_key: any, _value: string, row) {
                const roleItem = row as IAdminRbacRole;
                return (
                  <Tooltip title={roleItem.userList.join(' ')}>
                    <CommonButton
                      type="link"
                      onClick={() => {
                        navigator.clipboard
                          .writeText(roleItem.userList.join(' '))
                          .then(() => {
                            messageSuccess(getFormatMessage('copy_success'));
                          });
                      }}
                    >
                      {roleItem.userList.length}
                    </CommonButton>
                  </Tooltip>
                );
              },
            },
          },
          {
            key: 'updatedAt',
            name: getFormatMessage('modify_time_local'),
            tableExtension: {
              width: '150px',
              renderRow(_key: any, _value: string) {
                return getLocalTime(_value, userInfo?.UserSettings?.Timezone);
              },
            },
          },
          {
            key: 'action',
            name: getFormatMessage('operate'),
            tableExtension: {
              width: '200px',
              renderRow(_key: any, _value: string, row) {
                const isSuper = row.type === RbacRoleType.Owner;
                const isCustom = row.type === RbacRoleType.Custom;
                const isRowNoticeAdminRole =
                  row?.roleName === NOTICE_ADMIN_ROLE;
                if (isSuper) {
                  return '--';
                }

                const btns = [];

                if (
                  isUserSuper ||
                  (isUserAdmin && isCustom && !isRowNoticeAdminRole)
                ) {
                  btns.push(
                    <CommonButtonGroup>
                      <CommonButton
                        type="link"
                        onClick={() => {
                          editRole(row);
                        }}
                      >
                        {getFormatMessage('edit')}
                      </CommonButton>
                    </CommonButtonGroup>
                  );
                }

                if (
                  (isUserSuper && isCustom) ||
                  (isUserAdmin && isCustom && !isRowNoticeAdminRole)
                ) {
                  btns.push(
                    <AdminPermCheck
                      hasPerm={isUserSuper || isUserAdmin}
                      contentType="action"
                      otherTips={
                        row.userList.length > 0
                          ? getFormatMessage('cannot_delete_role_desc_tips')
                          : undefined
                      }
                    >
                      <CommonButton
                        disabled={row.userList.length > 0}
                        className={styles.delBtn}
                        type="link"
                        onClick={() => {
                          deleteRole(row);
                        }}
                      >
                        {getFormatMessage('delete')}
                      </CommonButton>
                    </AdminPermCheck>
                  );
                }

                if (btns.length === 0) {
                  return '--';
                }
                return <CommonButtonGroup>{btns}</CommonButtonGroup>;
              },
            },
          },
        ]}
      />
      {showDrawer && owner && adminRole && (
        <EditRoleDrawer
          members={members}
          commonRoles={commonRoles}
          viewOnly={drawerReadOnly}
          commonUsers={commonUsers}
          owner={owner}
          admin={adminRole}
          role={selectedRow}
          allUser={allUser}
          onSave={onSave}
          onCancel={closeDrawer}
        />
      )}
    </div>
  );
};
